import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers(['root', 'container', 'leftRegion', 'rightRegion']);

/**
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "container" | "leftRegion" | "rightRegion")[]
 * }>} SectionPanelStyles
 */
export const sectionPanelStyles = helpers.defineMultiStyleConfig({
  /**
   * @param {Object} args - Component arguments
   * @param {Contentful.SectionPanel} args.component - SectionPanel component data
   * @param {boolean} [args.isComplete] - Whether component has all fields
   * @param {boolean} [args.isFetching] - Whether additional data is being fetched
   *
   * @returns {SectionPanelStyles} - Component styles
   */
  baseStyle: ({component}) => {
    return {
      root: {
      },
      container: {
      },
      leftRegion: {
      },
      rightRegion: {
      },
    };
  },
  variants: {
    'Split Heading': (args) => ({
      container: {
        paddingTop: {base: '60px', md: args.component.paddingTop || '120px'},
        paddingBottom: {base: '40px', md: args.component.paddingBottom || '64px'},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: '16px', md: '40px'},
        sx: {
          '.component--rich-text-block h1, .component--rich-text-block h2, .component--rich-text-block h3, .component--rich-text-block h4, .component--rich-text-block h5, .component--rich-text-block h6': {
            fontSize: {base: '28px', md: '40px'},
            fontWeight: '500',
            lineHeight: '120%',

          },
          '.component--rich-text-block .chakra-text': {
            fontSize: {base: '16px', md: '18px'},
            fontWeight: '400',
            lineHeight: '160%',
          },
          '.chakra-link': {
            textDecoration: 'none',
            color: '#000',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '120%',
            padding: '14px 48px',
            borderRadius: '4px',
            border: '1px solid',
            borderColor: '#000',
            marginTop: '8px',
            _hover: {
              backgroundColor: '#000',
              color: '#fff',
              textDecoration: 'underline',
            },
          },
        },
      },
      leftRegion: {
        flex: {base: '1', md: '1'},
      },
      rightRegion: {
        flex: {base: '1', md: '1'},
      },
    }),
    '50/50': (args) => ({
      container: {
        paddingTop: {base: '60px', md: args.component.paddingTop || '120px'},
        paddingBottom: {base: '40px', md: args.component.paddingBottom || '70px'},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: '0', md: '40px'},
      },
      leftRegion: {
        flex: {base: '1', md: '1'},
      },
      rightRegion: {
        flex: {base: '1', md: '1'},
      },
    }),
    '60/40': (args) => ({
      container: {
        paddingTop: {base: '60px', md: args.component.paddingTop || '120px'},
        paddingBottom: {base: '40px', md: args.component.paddingBottom || '70px'},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: '0', md: '40px'},
      },
      leftRegion: {
        flex: {base: '1', md: '0 0 60%'},
      },
      rightRegion: {
        flex: {base: '1', md: '0 0 40%'},
      },
    }),
    '65/35': (args) => ({
      container: {
        paddingTop: {base: '60px', md: args.component.paddingTop || '120px'},
        paddingBottom: {base: '40px', md: args.component.paddingBottom || '70px'},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: '0', md: '40px'},
      },
      leftRegion: {
        flex: {base: '1', md: '0 0 65%'},
      },
      rightRegion: {
        flex: {base: '1', md: '0 0 35%'},
      },
    }),
    'King Difference Modular preview Block - (CF-013)': (args) => ({
      container: {
        paddingTop: {base: '48px', md: args.component.paddingTop || '120px'},
        paddingBottom: {base: '8px', md: args.component.paddingBottom || '53px'},
        sx: {
          '.component--rich-text-block': {
            textAlign: 'center',
            paddingLeft: {base: '0', md: '300px'},
            paddingRight: {base: '0', md: '300px'},
            paddingBottom: {base: '8px', md: '53px'},
          },
          '.component--rich-text-block .chakra-text:first-of-type': {
            textTransform: 'uppercase',
            fontSize: {base: '12px', md: '14px'},
            fontWeight: '500',
            lineHeight: '140%',
            letterSpacing: {base: '1.2px', md: '1.4px'},
          },
          '.component--rich-text-block h1': {
            fontSize: {base: '28px', md: '46px'},
            fontWeight: '500',
            lineHeight: '120%',
          },
          '.component--rich-text-block .chakra-text:not(:first-of-type)': {
            fontSize: {base: '16px', md: '20px'},
            fontWeight: '400',
            lineHeight: '150%',
          },
        },
      },
    }),
    'Support Block - (CF-019)': (args) => ({
      container: {
        gridTemplateColumns: {base: 'repeat(1, 1fr)', md: '290px 1fr 1fr'},
        gap: {base: '20px', md: '40px'},
        paddingTop: {base: '40px', md: args.component.paddingTop || '80px'},
        paddingBottom: {base: '40px', md: args.component.paddingBottom || '130px'},
        sx: {
          '> :last-of-type': {
            order: {base: '-1', md: '1'},
          },
          '.component--container ul': {
            marginLeft: '30px',
          },
          '.component--container .component--rich-text-block': {
            marginBottom: '24px',
          },
          '.component--container a': {
            fontSize: '14px',
            fontWeight: '600',
            lineHeight: '120%',
            textAlign: 'center',
            width: {base: '100%', md: 'auto'},
            marginRight: {base: '0', md: '14px'},
            marginBottom: {base: '8px', md: '14px'},
          },
          '.component--container .chakra-link .chakra-text': {
            fontSize: '14px',
            fontWeight: '600',
            lineHeight: '120%',
          },
          '.component--container a:last-of-type': {
            marginBottom: '0',
            marginRight: '0',
          },
          '.component--container .chakra-text': {
            color: 'brandColors.greyscale90',
            fontSize: '16px',
            fontWeight: '400',
            lineHeight: '160%',
          },
          '.component--rich-text-block h1, .component--rich-text-block h2, .component--rich-text-block h3, .component--rich-text-block h4, .component--rich-text-block h5, .component--rich-text-block h6': {
            color: 'brandColors.greyscale90',
            fontSize: {base: '28px', md: '40px'},
            fontWeight: '500',
            lineHeight: '120%',
          },
        },
      },
    }),
    'Editorial Feature Block - (CF-020) [1]': (args) => ({
      container: {
        paddingTop: {base: '16px', md: args.component.paddingTop || '40px'},
        paddingBottom: {base: '16px', md: args.component.paddingBottom || '40px'},
      },
    }),
    'Editorial Feature Block - (CF-020) [2]': (args) => ({
      container: {
        paddingTop: {base: '16px', md: args.component.paddingTop || '40px'},
        paddingBottom: {base: '16px', md: args.component.paddingBottom || '40px'},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: '0', md: '40px'},
      },
      leftRegion: {
        flex: {base: '1', md: '0 0 60%'},
      },
      rightRegion: {
        flex: {base: '1', md: '0 0 40%'},
      },
    }),
    'Editorial Feature Block - (CF-020) [3]': (args) => ({
      container: {
        paddingTop: {base: '16px', md: args.component.paddingTop || '40px'},
        paddingBottom: {base: '16px', md: args.component.paddingBottom || '40px'},
        gridTemplateColumns: {base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)'},
        gap: {base: '20px', md: '40px'},
      },
    }),
    'Editorial Copy - (CF-021)': (args) => ({
      container: {
        paddingTop: {base: '20px', md: args.component.paddingTop || '40px'},
        paddingBottom: {base: '20px', md: args.component.paddingBottom || '40px'},
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        sx: {
          '.component--rich-text-block': {
            width: {base: '100%', md: '620px'},
          },
          '.component--rich-text-block h1': {
            fontSize: {base: '22px', md: '32px'},
            fontWeight: '500',
            lineHeight: '120%',
            letterSpacing: '0',
          },
          '.component--rich-text-block > p.chakra-text': {
            fontSize: {base: '16px', md: '18px'},
            fontWeight: '400',
            lineHeight: '160%',
            letterSpacing: '0',
          },
          '.component--rich-text-block blockquote': {
            padding: {base: '0 0 0px 24px', md: '0 0 0px 32px'},
            borderLeft: '5px solid',
            borderColor: 'brandColors.primaryDarkLinen',
            fontSize: {base: '20px', md: '28px'},
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: {base: '150%', md: '140%'},
          },
        },
      },
    }),
    'Range/Category Tile Grid Block - (CF-030) [1]': (args) => ({
      container: {
        gridTemplateColumns: {base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)'},
        gap: 0,
        sx: {
          '> :last-of-type': {
            order: {base: '-1', md: '1'},
          },
        },
      },
    }),
    'Range/Category Tile Grid Block - (CF-030) [2]': (args) => ({
      container: {
        gridTemplateColumns: {base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'},
        gap: {base: '3px', md: '4px'},
      },
    }),
    'Form Block - (CF-058)': (args) => ({
      container: {
        paddingTop: {base: '32px', md: args.component.paddingTop || '85px'},
        paddingBottom: {base: '32px', md: args.component.paddingBottom || '85px'},
        sx: {
          '.component--rich-text-block .amb-markup': {
            display: 'flex',
            flexDirection: {base: 'column', md: 'row'},
            gap: {base: '0', md: '150px'},
            alignItems: {base: 'normal', md: 'flex-end'},
          },
          '.component--rich-text-block .amb-markup h1': {
            fontSize: {base: '40px', md: '72px'},
            fontWeight: {base: '400', md: '500'},
            lineHeight: {base: '120%', md: '100%'},
            flex: '1',
          },
          '.component--rich-text-block .amb-markup p': {
            fontSize: {base: '16px', md: '20px'},
            fontWeight: '400',
            lineHeight: {base: '160%', md: '150%'},
            flex: '1',
          },
        },
      },
    }),
  },
});
